/*
  SurfaceMaxx Soap Applicator (SMX-PWA208) calculator math.
  Pure functions only, no page code, so it can be tested with Node
  (tests/smx-soap-calc.test.js) and used in the browser as window.SmxSoapCalc.

  Method from the original instruction manual:
  1. Brass orifice from the pressure washer's GPM. Between ranges, use the larger one.
  2. Ideal ratio = starting chemical % / target %.
  3. In that orifice's column, use a proportioner whose ratio is within the
     close-match tolerance of the ideal ratio (the manual lists 12:1 for a
     12.5:1 need). If none is that close, use the next HIGHER ratio (milder).
     The next lower ratio is the stronger backup.
  4. Finished strength = starting chemical % / installed ratio.
*/
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.SmxSoapCalc = api;
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  // "0.8:1", "12", "12:1" -> number. Returns NaN when it can't be read.
  function parseRatio(value) {
    if (typeof value === 'number') return value;
    var text = String(value == null ? '' : value).trim().split(':')[0];
    if (text === '') return NaN;
    return Number(text);
  }

  // Number from user input. Accepts "3.2", "3,2" and "12.5%".
  function parseNumber(value) {
    if (typeof value === 'number') return value;
    var text = String(value == null ? '' : value).replace('%', '').replace(',', '.').trim();
    if (text === '' || !/^\d*\.?\d+$/.test(text)) return NaN;
    return Number(text);
  }

  // Rounds to 2 decimals and drops trailing zeros: 1.5625 -> "1.56", 6.25 -> "6.25", 8 -> "8".
  function format(n) {
    if (!isFinite(n)) return '';
    var rounded = Math.round((n + Number.EPSILON) * 100) / 100;
    return String(rounded);
  }

  // orifices: [{ id: '7', min: 1, max: 2.5 }, { id: '9', max: 3.5 }, { id: '13', max: 4.5 }]
  // The lowest GPM comes from the first orifice's min. Between ranges, the larger orifice wins,
  // so each orifice covers everything above the previous orifice's max.
  function pickOrifice(gpm, orifices) {
    if (!isFinite(gpm) || gpm <= 0 || !orifices || !orifices.length) return null;
    var min = orifices[0].min;
    if (gpm < min) return null;
    for (var i = 0; i < orifices.length; i++) {
      if (gpm <= orifices[i].max + 1e-9) return orifices[i];
    }
    return null;
  }

  // proportioners: [{ name, color, ratios: { '7': '0.8:1', ... }, recommend: true }]
  // Only proportioners with recommend !== false are suggested (the "open" setting is reference only).
  function candidatesFor(orificeId, proportioners) {
    var list = [];
    (proportioners || []).forEach(function (p) {
      if (p.recommend === false) return;
      var r = parseRatio(p.ratios && p.ratios[orificeId]);
      if (isFinite(r) && r > 0) list.push({ name: p.name, color: p.color, ratio: r, label: p.ratios[orificeId] });
    });
    list.sort(function (a, b) { return a.ratio - b.ratio; });
    return list;
  }

  /*
    input: { gpm, chemical, target, orifices, proportioners, tolerance (0.05 = 5%) }
    Returns { ok: false, error } or
    { ok: true, orifice, desired, match: 'exact' | 'close' | 'milder' | 'mildest-available',
      pick: { name, color, ratio, strength }, stronger: {...} | null }
  */
  function recommend(input) {
    var gpm = parseNumber(input.gpm);
    var chemical = parseNumber(input.chemical);
    var target = parseNumber(input.target);
    var tolerance = isFinite(input.tolerance) ? input.tolerance : 0.05;

    if (!isFinite(gpm)) return { ok: false, error: 'gpm-missing' };
    var orifice = pickOrifice(gpm, input.orifices);
    if (!orifice) return { ok: false, error: 'gpm-range' };
    if (!isFinite(chemical) || chemical <= 0 || chemical > 100) return { ok: false, error: 'chemical-invalid', orifice: orifice };
    if (!isFinite(target) || target <= 0) return { ok: false, error: 'target-invalid', orifice: orifice };
    if (target >= chemical) return { ok: false, error: 'target-too-high', orifice: orifice };

    var desired = chemical / target;
    var list = candidatesFor(orifice.id, input.proportioners);
    if (!list.length) return { ok: false, error: 'no-proportioners', orifice: orifice };

    var pick = null;
    var match = 'milder';

    // Exact or close match first (closest one wins).
    var bestDiff = Infinity;
    list.forEach(function (c) {
      var diff = Math.abs(c.ratio - desired) / desired;
      if (diff <= tolerance + 1e-9 && diff < bestDiff) { bestDiff = diff; pick = c; }
    });
    if (pick) {
      match = bestDiff < 1e-9 ? 'exact' : 'close';
    } else {
      // Next higher ratio = milder mix.
      for (var i = 0; i < list.length; i++) {
        if (list[i].ratio > desired) { pick = list[i]; break; }
      }
      if (!pick) {
        // Every option is stronger than the target: use the mildest one.
        pick = list[list.length - 1];
        match = 'mildest-available';
      }
    }

    // Stronger backup = next lower ratio than the pick.
    var stronger = null;
    for (var j = list.length - 1; j >= 0; j--) {
      if (list[j].ratio < pick.ratio) { stronger = list[j]; break; }
    }

    function withStrength(c) {
      return c ? { name: c.name, color: c.color, ratio: c.ratio, label: c.label, strength: chemical / c.ratio } : null;
    }

    return {
      ok: true,
      gpm: gpm,
      chemical: chemical,
      target: target,
      orifice: orifice,
      desired: desired,
      match: match,
      pick: withStrength(pick),
      stronger: withStrength(stronger)
    };
  }

  return { parseRatio: parseRatio, parseNumber: parseNumber, format: format, pickOrifice: pickOrifice, candidatesFor: candidatesFor, recommend: recommend };
});
